import React, { createContext, useContext, useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from 'react-router-dom'
import { createVisit, trackEvent, clearVisit, getVisitToken } from 'api/ahoy'
import AuthContext from './auth-context'

const AhoyContext = createContext({})

// How often the client reports that a session is still active. These heartbeats
// let the backend measure session duration (including time spent reading a page
// without clicking), and are excluded from user-facing event metrics.
const HEARTBEAT_INTERVAL_MS = 60 * 1000

export const AhoyProvider = ({ children }) => {
  const token = useContext(AuthContext)
  const { isAuthenticated, isLoading } = useAuth0()
  const location = useLocation()
  const visitInitialized = useRef(false)
  const previousPath = useRef(null)
  const currentPath = useRef(location.pathname)

  // Initialize Ahoy visit when user authenticates
  useEffect(() => {
    const initializeVisit = async () => {
      // Skip if already initialized to prevent multiple calls
      if (visitInitialized.current) {
        return
      }

      // Check if visit is already cached before creating a new one
      const existingVisit = getVisitToken()

      if (existingVisit) {
        // Visit token exists in cache, mark as initialized
        visitInitialized.current = true
        return
      }

      // Only create new visit if authenticated and not already initialized
      if (!isLoading && isAuthenticated && token) {
        try {
          await createVisit(token)
          visitInitialized.current = true
        } catch (error) {
          console.error('Failed to initialize Ahoy visit:', error)
        }
      }
    }

    initializeVisit()
  }, [isAuthenticated, isLoading, token])

  // Track page view events on route changes
  useEffect(() => {
    const trackPageView = async () => {
      if (
        isAuthenticated &&
        token &&
        visitInitialized.current &&
        location.pathname !== previousPath.current
      ) {
        try {
          await trackEvent({
            token,
            name: 'page_view',
            properties: {
              path: location.pathname,
              search: location.search,
              url: window.location.href,
              referrer: previousPath.current || document.referrer
            }
          })
            previousPath.current = location.pathname
        } catch (error) {
            console.error('Failed to track page view:', error)
        }
      }
    }

    trackPageView()
  }, [location.pathname, location.search, isAuthenticated, token])

  // Keep the latest path in a ref so the heartbeat can tag itself without
  // resetting its interval on every navigation.
  useEffect(() => {
    currentPath.current = location.pathname
  }, [location.pathname])

  // Emit periodic activity heartbeats so we can measure session duration. We
  // only ping while the tab is visible, and send one final ping when the tab
  // is hidden so the end of the session is captured even if it is never
  // re-focused.
  useEffect(() => {
    if (!isAuthenticated || !token) {
      return undefined
    }

    const pingActivity = () => {
      if (!visitInitialized.current) {
        return
      }
      trackEvent({
        token,
        name: 'app_heartbeat',
        properties: { path: currentPath.current }
      })
    }

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        pingActivity()
      }
    }, HEARTBEAT_INTERVAL_MS)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pingActivity()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, token])

  const value = {
    trackEvent: ({ name, properties }) => trackEvent({ token, name, properties }),
    clearVisit: () => {
      clearVisit()
      visitInitialized.current = false
      previousPath.current = null
    }
  }

  return <AhoyContext.Provider value={value}>{children}</AhoyContext.Provider>
}

export const useAhoy = () => {
  const context = useContext(AhoyContext)
  if (context === undefined) {
    throw new Error('useAhoy must be used within an AhoyProvider')
  }
  return context
}

export default AhoyContext
